/* ============================================================================
   The Vocabulary playground
   ----------------------------------------------------------------------------
   The token table used to be a printout. This is the version you can argue with.

   Three panels, each answering a question the experiments can't:

     Easing     Is `expo.out` actually the curve we want, and does our CSS
                cubic-bezier really match the GSAP name we paired it with?
                Both engines run the same token side by side on identical
                tracks. If they drift apart, the pairing in motion.css is wrong
                and this is where you find out — not in code review.

     Tokens     Every tunable value on a slider, with a specimen that fires as
                you drag. Edits are stored and re-applied on every page in the
                lab, so you can retune `--mo-slow` here and then go look at the
                PDP. When a value is settled, Copy CSS gives you the block to
                paste into motion.css, which stays the source of truth.

     Patterns   The motion primitives themselves — fade-rise, mask reveal,
                cross-fade, stagger, spring pulse, press, underline, drift.
                Each one runnable, each one listing the tokens it spends, each
                one copyable as the GSAP call that produced it.

   Nothing here invents a number. Every specimen reads MOTION, which reads
   motion.css. Break that and the playground starts lying.
   ========================================================================= */

import {
  MOTION,
  TUNABLE,
  isReduced,
  readOverrides,
  setOverride,
  clearOverrides,
  hasOverrides,
} from './motion.js';

/* --- Cubic-bezier maths -----------------------------------------------------
   Needed twice: to draw the curve, and to put a playhead on it that matches
   what the browser is actually doing. Standard Newton-Raphson solve for t
   given x, then evaluate y. Eight iterations is plenty at this precision. */

function parseBezier(value) {
  const v = String(value).trim();
  if (v === 'linear') return [0, 0, 1, 1];
  const match = v.match(/cubic-bezier\(([^)]+)\)/);
  if (!match) return [0.4, 0, 0.2, 1];
  const parts = match[1].split(',').map((n) => parseFloat(n));
  return parts.length === 4 && parts.every(Number.isFinite) ? parts : [0.4, 0, 0.2, 1];
}

function bezierEasing([x1, y1, x2, y2]) {
  const A = (a, b) => 1 - 3 * b + 3 * a;
  const B = (a, b) => 3 * b - 6 * a;
  const C = (a) => 3 * a;
  const calc = (t, a, b) => ((A(a, b) * t + B(a, b)) * t + C(a)) * t;
  const slope = (t, a, b) => 3 * A(a, b) * t * t + 2 * B(a, b) * t + C(a);

  if (x1 === y1 && x2 === y2) return (x) => x; // linear

  return (x) => {
    let t = x;
    for (let i = 0; i < 8; i += 1) {
      const s = slope(t, x1, x2);
      if (Math.abs(s) < 1e-6) break;
      t -= (calc(t, x1, x2) - x) / s;
      t = Math.min(1, Math.max(0, t));
    }
    return calc(t, y1, y2);
  };
}

/* --- Vocabulary definitions -------------------------------------------------
   Kept here rather than read from the DOM so the panel can be rebuilt after a
   token edit without re-parsing markup. */

const EASINGS = [
  ['standard', '--mo-ease-standard', 'power2.inOut', 'Default. State changes, colour, layout.'],
  ['entrance', '--mo-ease-entrance', 'expo.out', 'Things arriving. Decisive start, soft settle.'],
  ['exit', '--mo-ease-exit', 'expo.in', 'Things leaving. Soft start, decisive finish.'],
  ['emphasis', '--mo-ease-emphasis', 'expo.inOut', 'Large or full-bleed movement.'],
  ['spring', '--mo-ease-spring', 'back.out(1.7)', 'Affirmative moments only. Add-to-bag, favourite.'],
  ['linear', '--mo-ease-linear', 'none', 'Scroll-linked only. Never time-based.'],
];

const DURATIONS = ['instant', 'quick', 'base', 'slow', 'deliberate', 'cinematic'];

/** Slider ranges per token group. Units matter — CSS wants them back. */
const RANGES = {
  dur: { min: 0, max: 2000, step: 20, unit: 'ms', fixed: 0 },
  rise: { min: 0, max: 120, step: 2, unit: 'px', fixed: 0 },
  scale: { min: 0.85, max: 1.25, step: 0.005, unit: '', fixed: 3 },
  stagger: { min: 0, max: 320, step: 5, unit: 'ms', fixed: 0 },
  parallax: { min: 0, max: 0.6, step: 0.01, unit: '', fixed: 2 },
};

const GROUP_COPY = {
  dur: ['Duration', 'Named by intent, not by number. “Quick” should mean the same 200ms on a hover and on a toast.'],
  rise: ['Travel', 'How far an entering element moves. Too little and the motion reads as a twitch rather than an arrival.'],
  scale: ['Scale', 'Settle-from-zoom on reveal, and the tactile press. Keep these small — at full-bleed width 2% is a lot of pixels.'],
  stagger: ['Stagger', 'Per-item offset in a group reveal. Tight for dense grids, loose for a few large elements.'],
  parallax: ['Parallax depth', 'A fraction of scroll distance. Past about 0.3 the image visibly lags and people read it as broken, not deep.'],
};

const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
const cssValue = (token) => getComputedStyle(document.documentElement).getPropertyValue(token).trim();

/* ===========================================================================
   Panel 1 · Easing studio
   ======================================================================== */

function easingPanel(root, gsap) {
  root.innerHTML = `
    <div class="vp__lede">
      <p>
        Pick a curve. The plot is drawn from the real <code>cubic-bezier</code>
        in <code>motion.css</code>, and the playhead tracks the tween. The two
        tracks below run the same token through <b>CSS</b> and through
        <b>GSAP</b> at the same instant — our easings are declared as matched
        pairs, and this is the only way to confirm they actually are.
      </p>
      <p class="vp__aside">
        Drop the toolbar Speed to 0.2x before you judge anything. At 1x the
        difference between two candidates is a feeling; at a fifth speed it is
        a decision.
      </p>
    </div>

    <div class="ease">
      <div class="ease__list" role="radiogroup" aria-label="Easing token"></div>

      <div class="ease__stage">
        <svg class="ease__plot" viewBox="-10 -46 120 152" role="img" aria-label="Easing curve plot">
          <defs>
            <clipPath id="ease-clip"><rect x="-12" y="-48" width="126" height="156" /></clipPath>
          </defs>
          <g clip-path="url(#ease-clip)">
            <rect class="ease__field" x="0" y="0" width="100" height="100" />
            <line class="ease__axis" x1="0" y1="100" x2="100" y2="100" />
            <line class="ease__axis" x1="0" y1="0" x2="0" y2="100" />
            <line class="ease__axis ease__axis--ghost" x1="0" y1="0" x2="100" y2="0" />
            <path class="ease__curve ease__curve--ghost" data-ghost-curve d="" />
            <path class="ease__curve" data-curve d="" />
            <circle class="ease__head" data-head r="3.2" cx="0" cy="100" />
          </g>
          <text class="ease__tick" x="-3" y="103" text-anchor="end">0</text>
          <text class="ease__tick" x="-3" y="3" text-anchor="end">1</text>
          <text class="ease__tick" x="100" y="112" text-anchor="middle">time</text>
        </svg>

        <div class="ease__meta">
          <div class="ease__name u-caps" data-ease-name></div>
          <p class="ease__desc" data-ease-desc></p>
          <dl class="ease__pair">
            <div><dt>CSS</dt><dd data-ease-css></dd></div>
            <div><dt>GSAP</dt><dd data-ease-gsap></dd></div>
          </dl>
        </div>
      </div>

      <div class="ease__controls">
        <div class="ease__durations" role="radiogroup" aria-label="Duration"></div>
        <label class="ease__compare">
          <span>Ghost</span>
          <select data-compare aria-label="Compare against a second easing">
            <option value="">none</option>
            ${EASINGS.map(([key]) => `<option value="${key}">${key}</option>`).join('')}
          </select>
        </label>
        <button class="vp__btn vp__btn--go" data-run>Run both</button>
      </div>

      <div class="ease__races" data-races></div>
    </div>
  `;

  const list = root.querySelector('.ease__list');
  const durations = root.querySelector('.ease__durations');
  const races = root.querySelector('[data-races]');
  const curve = root.querySelector('[data-curve]');
  const ghostCurve = root.querySelector('[data-ghost-curve]');
  const head = root.querySelector('[data-head]');
  const compare = root.querySelector('[data-compare]');

  let easeKey = 'entrance';
  let durKey = 'deliberate';

  list.innerHTML = EASINGS.map(
    ([key, token]) => `
    <button class="ease__chip" role="radio" data-ease="${key}" aria-checked="${key === easeKey}">
      <span class="ease__chip-name">${key}</span>
      <span class="ease__chip-val">${esc(cssValue(token))}</span>
    </button>`
  ).join('');

  durations.innerHTML = DURATIONS.map(
    (key) => `
    <button class="vp__pill" role="radio" data-dur="${key}" aria-checked="${key === durKey}">
      ${key}<b data-dur-readout="${key}"></b>
    </button>`
  ).join('');

  // Two lanes per easing under test: one driven by a CSS transition, one by a
  // GSAP tween. Same distance, same duration, fired in the same frame.
  const lane = (key, label, variant) => `
    <div class="race" data-race="${key}">
      <div class="race__label"><b>${key}</b> ${label}</div>
      <div class="race__track race__track--${variant}">
        <span class="race__dot" data-dot="${key}-css" title="CSS transition"></span>
        <span class="race__dot race__dot--gsap" data-dot="${key}-gsap" title="GSAP tween"></span>
      </div>
      <div class="race__legend"><i class="race__key"></i>CSS<i class="race__key race__key--gsap"></i>GSAP</div>
    </div>`;

  function drawCurve(path, key) {
    if (!key) {
      path.setAttribute('d', '');
      return null;
    }
    const token = EASINGS.find(([k]) => k === key)[1];
    const [x1, y1, x2, y2] = parseBezier(cssValue(token));
    // SVG y grows downward, so 1 maps to 0 and 0 maps to 100.
    path.setAttribute(
      'd',
      `M 0 100 C ${x1 * 100} ${100 - y1 * 100} ${x2 * 100} ${100 - y2 * 100} 100 0`
    );
    return bezierEasing([x1, y1, x2, y2]);
  }

  function paintMeta() {
    const [, token, gsapName, desc] = EASINGS.find(([k]) => k === easeKey);
    root.querySelector('[data-ease-name]').textContent = easeKey;
    root.querySelector('[data-ease-desc]').textContent = desc;
    root.querySelector('[data-ease-css]').textContent = cssValue(token);
    root.querySelector('[data-ease-gsap]').textContent = gsapName;

    races.innerHTML =
      lane(easeKey, 'under test', 'primary') +
      (compare.value && compare.value !== easeKey ? lane(compare.value, 'ghost', 'ghost') : '');
  }

  function paintDurations() {
    DURATIONS.forEach((key) => {
      const el = root.querySelector(`[data-dur-readout="${key}"]`);
      if (el) el.textContent = `${Math.round(MOTION.dur[key] * 1000)}ms`;
    });
  }

  function repaint() {
    const solve = drawCurve(curve, easeKey);
    drawCurve(ghostCurve, compare.value && compare.value !== easeKey ? compare.value : null);
    paintMeta();
    paintDurations();
    return solve;
  }

  let solveY = repaint();

  /* --- Running the race --------------------------------------------------- */
  function run() {
    const duration = MOTION.dur[durKey];
    const keys = [easeKey, compare.value && compare.value !== easeKey ? compare.value : null].filter(
      Boolean
    );

    keys.forEach((key) => {
      const [, token, gsapName] = EASINGS.find(([k]) => k === key);
      const cssDot = root.querySelector(`[data-dot="${key}-css"]`);
      const gsapDot = root.querySelector(`[data-dot="${key}-gsap"]`);
      if (!cssDot || !gsapDot) return;

      const distance = cssDot.parentElement.offsetWidth - 22;

      // CSS lane. Reset with no transition, force a reflow so the browser
      // registers the start position, then transition. Skipping the reflow is
      // why "it only animates the first time" happens.
      cssDot.style.transition = 'none';
      cssDot.style.transform = 'translateX(0px)';
      void cssDot.offsetWidth;
      cssDot.style.transition = `transform ${duration}s ${cssValue(token)}`;
      cssDot.style.transform = `translateX(${distance}px)`;

      // GSAP lane, same frame.
      gsap.fromTo(
        gsapDot,
        { x: 0 },
        { x: distance, duration, ease: MOTION.ease[key] ?? gsapName, overwrite: true }
      );
    });

    // Playhead. Driven by a linear tween whose progress is fed through the
    // same solve the plot was drawn from, so the dot sits on the curve.
    if (solveY) {
      gsap.killTweensOf(head);
      const progress = { t: 0 };
      gsap.to(progress, {
        t: 1,
        duration,
        ease: 'none',
        overwrite: true,
        onUpdate: () => {
          head.setAttribute('cx', progress.t * 100);
          head.setAttribute('cy', 100 - solveY(progress.t) * 100);
        },
      });
    }
  }

  /* --- Wiring ------------------------------------------------------------- */
  list.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-ease]');
    if (!btn) return;
    easeKey = btn.dataset.ease;
    list.querySelectorAll('[data-ease]').forEach((b) =>
      b.setAttribute('aria-checked', String(b.dataset.ease === easeKey))
    );
    solveY = repaint();
    run();
  });

  durations.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-dur]');
    if (!btn) return;
    durKey = btn.dataset.dur;
    durations.querySelectorAll('[data-dur]').forEach((b) =>
      b.setAttribute('aria-checked', String(b.dataset.dur === durKey))
    );
    run();
  });

  compare.addEventListener('change', () => {
    solveY = repaint();
    run();
  });

  root.querySelector('[data-run]').addEventListener('click', run);

  return {
    refresh: () => {
      list.querySelectorAll('[data-ease]').forEach((btn) => {
        const token = EASINGS.find(([k]) => k === btn.dataset.ease)[1];
        btn.querySelector('.ease__chip-val').textContent = cssValue(token);
      });
      solveY = repaint();
    },
  };
}

/* ===========================================================================
   Panel 2 · Token studio
   ======================================================================== */

function tokenPanel(root, gsap) {
  root.innerHTML = `
    <div class="vp__lede">
      <p>
        Drag a value and the specimen re-fires. Edits apply to
        <b>every page in the lab</b> — retune <code>--mo-slow</code> here, then
        open the PDP and see what it did to the buy rail. Nothing is written to
        <code>motion.css</code>; when a number is settled, export it and paste
        it into the file, which stays the source of truth.
      </p>
      <p class="vp__aside" data-override-note hidden></p>
    </div>

    <div class="vp__bar">
      <button class="vp__btn vp__btn--go" data-copy-css>Copy CSS</button>
      <button class="vp__btn" data-reset-all>Reset all</button>
      <span class="vp__status" data-status role="status"></span>
    </div>

    <div class="studio" data-studio></div>
  `;

  const studio = root.querySelector('[data-studio]');
  const status = root.querySelector('[data-status]');
  const note = root.querySelector('[data-override-note]');

  const SPECIMENS = {
    dur: '<span class="spec__bar" data-spec-el></span>',
    rise: '<span class="spec__chip" data-spec-el>Arrive</span>',
    scale: '<span class="spec__square" data-spec-el></span>',
    stagger: Array.from({ length: 6 }, () => '<span class="spec__dot" data-spec-el></span>').join(''),
    parallax:
      '<span class="spec__layer spec__layer--far" data-spec-el></span>' +
      '<span class="spec__layer spec__layer--mid" data-spec-el></span>' +
      '<span class="spec__layer spec__layer--near" data-spec-el></span>',
  };

  studio.innerHTML = Object.entries(TUNABLE)
    .map(([group, tokens]) => {
      const [title, blurb] = GROUP_COPY[group];
      const r = RANGES[group];
      return `
      <section class="studio__group" data-group="${group}">
        <header class="studio__head">
          <h3 class="u-caps">${title}</h3>
          <p>${blurb}</p>
        </header>

        <div class="studio__rows">
          ${tokens
            .map(
              (token) => `
            <div class="studio__row" data-token="${token}">
              <code class="studio__name">${token}</code>
              <input class="studio__range" type="range"
                     min="${r.min}" max="${r.max}" step="${r.step}"
                     aria-label="${token}">
              <output class="studio__out" data-out></output>
              <button class="studio__revert" data-revert title="Revert to motion.css" hidden>&#8634;</button>
            </div>`
            )
            .join('')}
        </div>

        <div class="studio__specimen">
          <div class="spec spec--${group}" data-spec>${SPECIMENS[group]}</div>
          <button class="vp__btn" data-play>Run</button>
        </div>
      </section>`;
    })
    .join('');

  /* --- Specimens ----------------------------------------------------------
     Each one spends only the tokens its own group owns, so a change is
     attributable. The duration specimen uses `base` as its reference travel,
     the rise specimen uses `slow`, and so on — stated here rather than left
     for someone to reverse-engineer from the tween. */
  function play(group) {
    const scope = studio.querySelector(`[data-group="${group}"] [data-spec]`);
    const els = gsap.utils.toArray(scope.querySelectorAll('[data-spec-el]'));
    gsap.killTweensOf(els);
    const width = scope.offsetWidth;

    if (group === 'dur') {
      // One bar, run once per duration token, so the six lengths are visible
      // against each other rather than described.
      const el = els[0];
      gsap.fromTo(
        el,
        { width: 0 },
        { width: width - 2, duration: MOTION.dur.base, ease: MOTION.ease.entrance, overwrite: true }
      );
      return;
    }

    if (group === 'rise') {
      gsap.fromTo(
        els,
        { y: MOTION.rise.md, opacity: 0 },
        { y: 0, opacity: 1, duration: MOTION.dur.slow, ease: MOTION.ease.entrance, overwrite: true }
      );
      return;
    }

    if (group === 'scale') {
      gsap
        .timeline()
        .fromTo(
          els,
          { scale: MOTION.scale.in, opacity: 0 },
          { scale: 1, opacity: 1, duration: MOTION.dur.slow, ease: MOTION.ease.entrance }
        )
        .to(els, { scale: MOTION.scale.hover, duration: MOTION.dur.quick })
        .to(els, { scale: MOTION.scale.press, duration: MOTION.dur.instant })
        .to(els, { scale: 1, duration: MOTION.dur.quick });
      return;
    }

    if (group === 'stagger') {
      gsap.fromTo(
        els,
        { y: MOTION.rise.sm, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: MOTION.dur.slow,
          ease: MOTION.ease.entrance,
          stagger: MOTION.stagger.base,
          overwrite: true,
        }
      );
      return;
    }

    if (group === 'parallax') {
      // Depth is a fraction of scroll; here it's a fraction of one sweep, so
      // the relative separation reads the same way it will on the page.
      const depths = [MOTION.parallax.far, MOTION.parallax.mid, MOTION.parallax.near];
      els.forEach((el, i) => {
        gsap.fromTo(
          el,
          { xPercent: depths[i] * 220 },
          {
            xPercent: -depths[i] * 220,
            duration: MOTION.dur.cinematic,
            ease: MOTION.ease.standard,
            yoyo: true,
            repeat: 1,
            overwrite: true,
          }
        );
      });
    }
  }

  /* --- Reading and writing ------------------------------------------------ */
  function syncRow(row) {
    const token = row.dataset.token;
    const group = row.closest('[data-group]').dataset.group;
    const r = RANGES[group];
    const raw = parseFloat(cssValue(token));
    const range = row.querySelector('.studio__range');
    const out = row.querySelector('[data-out]');
    const revert = row.querySelector('[data-revert]');
    const overridden = token in readOverrides();

    range.value = String(raw);
    out.textContent = `${raw.toFixed(r.fixed)}${r.unit}`;
    row.dataset.overridden = String(overridden);
    revert.hidden = !overridden;
  }

  function syncAll() {
    studio.querySelectorAll('.studio__row').forEach(syncRow);
    const n = Object.keys(readOverrides()).length;
    note.hidden = n === 0;
    note.innerHTML = n
      ? `<b>${n} token${n === 1 ? '' : 's'} tuned.</b> These are live on every page in the lab until you reset or paste them into <code>motion.css</code>.`
      : '';
  }

  function say(message) {
    status.textContent = message;
    clearTimeout(say.timer);
    say.timer = setTimeout(() => {
      status.textContent = '';
    }, 2600);
  }

  let playTimer;
  studio.addEventListener('input', (e) => {
    const range = e.target.closest('.studio__range');
    if (!range) return;
    const row = range.closest('.studio__row');
    const group = row.closest('[data-group]').dataset.group;
    const r = RANGES[group];
    const value = `${Number(range.value).toFixed(r.fixed)}${r.unit}`;

    setOverride(row.dataset.token, value);
    row.querySelector('[data-out]').textContent = value;
    row.dataset.overridden = 'true';
    row.querySelector('[data-revert]').hidden = false;

    // Fire the specimen once the drag pauses. Re-running on every input event
    // turns the slider into a strobe and tells you nothing.
    clearTimeout(playTimer);
    playTimer = setTimeout(() => {
      play(group);
      syncAll();
    }, 140);
  });

  studio.addEventListener('click', (e) => {
    const playBtn = e.target.closest('[data-play]');
    if (playBtn) {
      play(playBtn.closest('[data-group]').dataset.group);
      return;
    }
    const revert = e.target.closest('[data-revert]');
    if (revert) {
      const row = revert.closest('.studio__row');
      setOverride(row.dataset.token, null);
      syncAll();
      play(row.closest('[data-group]').dataset.group);
    }
  });

  root.querySelector('[data-reset-all]').addEventListener('click', () => {
    clearOverrides();
    syncAll();
    say('Reverted to motion.css.');
  });

  /* --- Export -------------------------------------------------------------
     Emits the whole :root block, not just the edits. A partial block invites
     someone to paste it in as an addition and end up with two competing
     definitions of the same token. */
  root.querySelector('[data-copy-css]').addEventListener('click', async () => {
    const block = Object.entries(TUNABLE)
      .map(([group, tokens]) => {
        const [title] = GROUP_COPY[group];
        const lines = tokens.map((t) => `  ${t}: ${cssValue(t)};`).join('\n');
        return `  /* --- ${title} --- */\n${lines}`;
      })
      .join('\n\n');

    const easings = EASINGS.map(([, token, gsapName]) => `  ${token}: ${cssValue(token)}; /* GSAP: ${gsapName} */`).join('\n');
    const out = `:root {\n${block}\n\n  /* --- Easing --- */\n${easings}\n}\n`;

    try {
      await navigator.clipboard.writeText(out);
      say('Copied. Paste over the :root block in src/motion.css.');
    } catch {
      // Clipboard is blocked on insecure origins and in some embedded views.
      console.info('[vocabulary] clipboard unavailable — dumping instead:\n' + out);
      say('Clipboard blocked — the CSS is in the console.');
    }
  });

  syncAll();
  return { refresh: syncAll };
}

/* ===========================================================================
   Panel 3 · Pattern shelf
   ======================================================================== */

const PATTERNS = [
  {
    key: 'fade-rise',
    name: 'Fade rise',
    use: 'The workhorse. Section headers, cards, anything arriving on scroll.',
    spends: ['--mo-rise-md', '--mo-slow', '--mo-ease-entrance'],
    markup: '<span class="pat__block" data-el>Arrive</span>',
    code: (m) =>
      `gsap.fromTo(el,\n  { y: MOTION.rise.md, opacity: 0 },\n  { y: 0, opacity: 1, duration: MOTION.dur.slow, ease: MOTION.ease.entrance }\n);`,
    run: (gsap, els) =>
      gsap.fromTo(
        els,
        { y: MOTION.rise.md, opacity: 0 },
        { y: 0, opacity: 1, duration: MOTION.dur.slow, ease: MOTION.ease.entrance, overwrite: true }
      ),
  },
  {
    key: 'mask-reveal',
    name: 'Mask reveal',
    use: 'Headline lines, captions, nav. The wrapper clips; the line climbs out.',
    spends: ['--mo-deliberate', '--mo-ease-entrance', '--mo-stagger-base'],
    markup:
      '<span class="mo-mask pat__line"><span data-el>Built for</span></span>' +
      '<span class="mo-mask pat__line"><span data-el>awesome humans</span></span>',
    code: () =>
      `gsap.fromTo(lines,\n  { yPercent: 120 },\n  { yPercent: 0, duration: MOTION.dur.deliberate,\n    ease: MOTION.ease.entrance, stagger: MOTION.stagger.base }\n);`,
    run: (gsap, els) =>
      gsap.fromTo(
        els,
        { yPercent: 120 },
        {
          yPercent: 0,
          duration: MOTION.dur.deliberate,
          ease: MOTION.ease.entrance,
          stagger: MOTION.stagger.base,
          overwrite: true,
        }
      ),
  },
  {
    key: 'cross-fade',
    name: 'Label cross-fade',
    use: 'Swapping text in place — colour name, price, filter count. A hard swap reads as a glitch.',
    spends: ['--mo-quick', '--mo-base', '--mo-ease-exit', '--mo-ease-entrance'],
    markup: '<span class="pat__label" data-el>Navy</span>',
    code: () =>
      `gsap.timeline()\n  .to(label, { opacity: 0, y: -4, duration: MOTION.dur.quick, ease: MOTION.ease.exit })\n  .set(label, { textContent: next })\n  .to(label, { opacity: 1, y: 0, duration: MOTION.dur.base, ease: MOTION.ease.entrance });`,
    run: (gsap, els) => {
      const names = ['Navy', 'Ceil Blue', 'Burgundy', 'Hunter Green', 'Graphite'];
      const el = els[0];
      const next = names[(names.indexOf(el.textContent.trim()) + 1) % names.length];
      return gsap
        .timeline()
        .to(el, { opacity: 0, y: -4, duration: MOTION.dur.quick, ease: MOTION.ease.exit })
        .set(el, { textContent: next })
        .to(el, { opacity: 1, y: 0, duration: MOTION.dur.base, ease: MOTION.ease.entrance });
    },
  },
  {
    key: 'stagger-in',
    name: 'Grid stagger',
    use: 'PLP cards by row. Batch them — a 24-item stagger at 80ms is a two-second wait.',
    spends: ['--mo-rise-sm', '--mo-slow', '--mo-stagger-tight'],
    markup: Array.from({ length: 8 }, () => '<span class="pat__tile" data-el></span>').join(''),
    code: () =>
      `ScrollTrigger.batch(cards, {\n  start: 'top 88%', once: true, batchMax: 6,\n  onEnter: (group) => gsap.to(group, {\n    y: 0, opacity: 1, duration: MOTION.dur.slow,\n    ease: MOTION.ease.entrance, stagger: MOTION.stagger.tight\n  })\n});`,
    run: (gsap, els) =>
      gsap.fromTo(
        els,
        { y: MOTION.rise.sm, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: MOTION.dur.slow,
          ease: MOTION.ease.entrance,
          stagger: MOTION.stagger.tight,
          overwrite: true,
        }
      ),
  },
  {
    key: 'spring-pulse',
    name: 'Spring confirm',
    use: 'Add-to-bag, favourite. The one place overshoot is earned — it says “yes, that worked.”',
    spends: ['--mo-quick', '--mo-slow', '--mo-ease-spring'],
    markup: '<button class="pat__cta" type="button" data-el><span data-cta-label>Add to bag</span></button>',
    code: () =>
      `gsap.timeline()\n  .to(label, { yPercent: -120, opacity: 0, duration: MOTION.dur.quick, ease: MOTION.ease.exit })\n  .set(label, { textContent: 'Added to bag', yPercent: 120 })\n  .to(label, { yPercent: 0, opacity: 1, duration: MOTION.dur.slow, ease: MOTION.ease.spring })\n  .fromTo(button, { scale: 1 },\n    { scale: 1.015, duration: MOTION.dur.quick, yoyo: true, repeat: 1 }, '<');`,
    run: (gsap, els, scope) => {
      const button = els[0];
      const label = scope.querySelector('[data-cta-label]');
      const added = label.textContent.trim() === 'Added to bag';
      return gsap
        .timeline()
        .to(label, { yPercent: -120, opacity: 0, duration: MOTION.dur.quick, ease: MOTION.ease.exit })
        .set(label, { textContent: added ? 'Add to bag' : 'Added to bag', yPercent: 120 })
        .to(label, {
          yPercent: 0,
          opacity: 1,
          duration: MOTION.dur.slow,
          ease: MOTION.ease.spring,
        })
        .fromTo(
          button,
          { scale: 1 },
          { scale: 1.015, duration: MOTION.dur.quick, yoyo: true, repeat: 1, ease: MOTION.ease.standard },
          '<'
        );
    },
  },
  {
    key: 'press',
    name: 'Press',
    use: 'Tactile feedback on tap. CSS-only — it must respond before any JavaScript could.',
    spends: ['--mo-scale-press', '--mo-instant'],
    markup: '<button class="pat__cta pat__cta--ghost mo-press" type="button" data-el>Press and hold</button>',
    code: () =>
      `.mo-press:active {\n  transform: scale(var(--mo-scale-press));\n  transition: transform var(--mo-instant) var(--mo-ease-standard);\n}`,
    run: (gsap, els) =>
      gsap
        .timeline()
        .to(els, { scale: MOTION.scale.press, duration: MOTION.dur.instant, ease: MOTION.ease.standard })
        .to(els, { scale: 1, duration: MOTION.dur.quick, ease: MOTION.ease.entrance }),
  },
  {
    key: 'underline',
    name: 'Underline wipe',
    use: 'Text links and nav. Background-size, not a pseudo-element — it wraps correctly across lines.',
    spends: ['--mo-base', '--mo-ease-standard'],
    markup: '<a class="pat__link mo-underline" href="#vocabulary" data-el>Shop the collection</a>',
    code: () =>
      `.mo-underline {\n  background-image: linear-gradient(currentColor, currentColor);\n  background-size: 0% 1px;\n  background-position: 0 100%;\n  background-repeat: no-repeat;\n  transition: background-size var(--mo-transition-base);\n}\n.mo-underline:hover { background-size: 100% 1px; }`,
    run: (gsap, els) => {
      const el = els[0];
      el.style.transition = 'none';
      el.style.backgroundSize = '0% 1px';
      void el.offsetWidth;
      el.style.transition = `background-size ${MOTION.dur.base}s ${cssValue('--mo-ease-standard')}`;
      el.style.backgroundSize = '100% 1px';
      return null;
    },
  },
  {
    key: 'drawer-slide',
    name: 'Drawer slide-in',
    use: 'Cart, filters, account. Panel enters first; the scrim settles behind it — never the other way round.',
    spends: ['--mo-slow', '--mo-base', '--mo-ease-entrance'],
    markup:
      '<span class="pat__scrim" data-el></span>' +
      '<span class="pat__drawer" data-el><b>Your bag</b><span>Free returns</span></span>',
    code: () =>
      `gsap.timeline()\n  .fromTo(scrim, { opacity: 0 }, { opacity: 1, duration: MOTION.dur.base, ease: MOTION.ease.standard })\n  .fromTo(panel, { xPercent: 100 },\n    { xPercent: 0, duration: MOTION.dur.slow, ease: MOTION.ease.entrance }, '<0.05');`,
    run: (gsap, els) => {
      const [scrim, drawer] = els;
      return gsap
        .timeline()
        .fromTo(scrim, { opacity: 0 }, { opacity: 1, duration: MOTION.dur.base, ease: MOTION.ease.standard })
        .fromTo(
          drawer,
          { xPercent: 100 },
          { xPercent: 0, duration: MOTION.dur.slow, ease: MOTION.ease.entrance },
          '<0.05'
        );
    },
  },
  {
    key: 'fly-to-cart',
    name: 'Fly-to-cart cue',
    use: 'Answers "where did that thing go?" without the user having to look. Cheap, delightful, once per add.',
    spends: ['--mo-deliberate', '--mo-quick', '--mo-ease-emphasis'],
    markup:
      '<span class="pat__cart-target" aria-hidden="true">◔</span>' +
      '<span class="pat__fly" data-el></span>',
    code: () =>
      `gsap.timeline()\n  .to(clone, { x: dx, y: dy, scale: 0.2,\n    duration: MOTION.dur.deliberate, ease: MOTION.ease.emphasis })\n  .to(clone, { opacity: 0, duration: MOTION.dur.quick, ease: MOTION.ease.exit }, '-=0.18');`,
    run: (gsap, els) => {
      const fly = els[0];
      const stage = fly.parentElement;
      const target = stage.querySelector('.pat__cart-target');
      const targetRect = target.getBoundingClientRect();
      const flyRect = fly.getBoundingClientRect();
      const dx = targetRect.left + targetRect.width / 2 - (flyRect.left + flyRect.width / 2);
      const dy = targetRect.top + targetRect.height / 2 - (flyRect.top + flyRect.height / 2);
      gsap.set(fly, { x: 0, y: 0, scale: 1, opacity: 1 });
      return gsap
        .timeline()
        .to(fly, {
          x: dx,
          y: dy,
          scale: 0.2,
          duration: MOTION.dur.deliberate,
          ease: MOTION.ease.emphasis,
        })
        .to(fly, { opacity: 0, duration: MOTION.dur.quick, ease: MOTION.ease.exit }, '-=0.18');
    },
  },
  {
    key: 'sticky-state',
    name: 'Sticky-header state',
    use: 'Transparent-over-hero → filled-with-border. A cross-fade, never a hard cut. Wordmark tightens a hair as the second cue.',
    spends: ['--mo-base', '--mo-ease-standard'],
    markup:
      '<span class="pat__nav" data-el>' +
      '<b class="pat__nav-mark">FIGS</b>' +
      '<span class="pat__nav-links"><i>Women</i><i>Men</i><i>Stories</i></span>' +
      '</span>',
    code: () =>
      `nav.dataset.solid = 'true';\n// CSS:\n.nav { transition: background var(--mo-base) var(--mo-ease-standard),\n                    color var(--mo-base) var(--mo-ease-standard),\n                    border-color var(--mo-base) var(--mo-ease-standard); }\n.nav[data-solid='true'] { background: white; color: var(--text-primary);\n                          border-bottom: 1px solid var(--border-subtle); }`,
    run: (_gsap, els) => {
      const nav = els[0];
      const toggle = () => (nav.dataset.solid = nav.dataset.solid === 'true' ? 'false' : 'true');
      toggle();
      setTimeout(toggle, 1400);
      return null;
    },
  },
  {
    key: 'mega-panel',
    name: 'Mega-menu open',
    use: 'Panel drops 8px and fades; columns stagger inside. Small y-travel on a wide element reads as menu, not layout shift.',
    spends: ['--mo-base', '--mo-slow', '--mo-stagger-tight', '--mo-ease-entrance'],
    markup:
      '<span class="pat__mega" data-el>' +
      '<span class="pat__mega-col" data-mega-col><b>Tops</b><i>Casma</i><i>Catarina</i></span>' +
      '<span class="pat__mega-col" data-mega-col><b>Pants</b><i>Zamora</i><i>Kade</i></span>' +
      '<span class="pat__mega-col" data-mega-col><b>Jackets</b><i>Marlowe</i></span>' +
      '</span>',
    code: () =>
      `gsap.timeline()\n  .fromTo(panel, { autoAlpha: 0, y: -8 },\n    { autoAlpha: 1, y: 0, duration: MOTION.dur.base, ease: MOTION.ease.entrance })\n  .fromTo(cols, { autoAlpha: 0, y: MOTION.rise.xs },\n    { autoAlpha: 1, y: 0, duration: MOTION.dur.slow,\n      ease: MOTION.ease.entrance, stagger: MOTION.stagger.tight }, '<0.08');`,
    run: (gsap, els, scope) => {
      const panel = els[0];
      const cols = scope.querySelectorAll('[data-mega-col]');
      return gsap
        .timeline()
        .fromTo(
          panel,
          { autoAlpha: 0, y: -8 },
          { autoAlpha: 1, y: 0, duration: MOTION.dur.base, ease: MOTION.ease.entrance }
        )
        .fromTo(
          cols,
          { autoAlpha: 0, y: MOTION.rise.xs },
          {
            autoAlpha: 1,
            y: 0,
            duration: MOTION.dur.slow,
            ease: MOTION.ease.entrance,
            stagger: MOTION.stagger.tight,
          },
          '<0.08'
        );
    },
  },
  {
    key: 'filter-reveal',
    name: 'Filter reveal',
    use: 'Sticky bar slides down once scrolled past the header — acts as the seam between editorial and grid.',
    spends: ['--mo-base', '--mo-ease-entrance'],
    markup:
      '<span class="pat__filters" data-el>' +
      '<i>All</i><i>FIONx</i><i>FORMx</i><i>Tops</i>' +
      '</span>',
    code: () =>
      `ScrollTrigger.create({\n  trigger: '.lookbook',\n  start: 'bottom top+=60',\n  onEnter: () => (bar.dataset.revealed = 'true'),\n  onLeaveBack: () => (bar.dataset.revealed = 'false'),\n});\n// CSS: .filters { transform: translateY(-100%); transition: transform var(--mo-base) var(--mo-ease-entrance); }`,
    run: (gsap, els) => {
      const bar = els[0];
      return gsap.fromTo(
        bar,
        { yPercent: -100, opacity: 0 },
        { yPercent: 0, opacity: 1, duration: MOTION.dur.base, ease: MOTION.ease.entrance }
      );
    },
  },
  {
    key: 'flip-density',
    name: 'FLIP density swap',
    use: 'Grid density toggle — cards animate from old rect to new rect. Without FLIP the reflow reads as a page load.',
    spends: ['--mo-slow', '--mo-stagger-tight', '--mo-ease-entrance'],
    markup: Array.from({ length: 6 }, (_, i) => `<span class="pat__flip-tile" data-el data-i="${i}"></span>`).join(''),
    code: () =>
      `const state = Flip.getState(cards);\ngrid.dataset.density = next;\nFlip.from(state, {\n  duration: MOTION.dur.slow,\n  ease: MOTION.ease.entrance,\n  stagger: MOTION.stagger.tight,\n  absolute: true,\n});`,
    run: (gsap, els, scope) => {
      if (!window.gsap?.Flip) return null;
      const stage = scope;
      const dense = stage.dataset.dense === 'true';
      const state = window.gsap.Flip.getState(els);
      stage.dataset.dense = dense ? 'false' : 'true';
      return window.gsap.Flip.from(state, {
        duration: MOTION.dur.slow,
        ease: MOTION.ease.entrance,
        stagger: MOTION.stagger.tight,
        absolute: true,
      });
    },
  },
  {
    key: 'drift',
    name: 'Parallax drift',
    use: 'Gallery depth. Scrubbed against the element’s own travel, so it behaves the same on a 13" laptop and a 27" display.',
    spends: ['--mo-parallax-near', '--mo-parallax-mid', '--mo-parallax-far'],
    markup:
      '<span class="pat__depth pat__depth--far" data-el></span>' +
      '<span class="pat__depth pat__depth--mid" data-el></span>' +
      '<span class="pat__depth pat__depth--near" data-el></span>',
    code: () =>
      `gsap.fromTo(shot,\n  { yPercent: depth * 50 },\n  { yPercent: -depth * 50, ease: 'none',\n    scrollTrigger: { trigger: shot, start: 'top bottom', end: 'bottom top', scrub: true } }\n);`,
    run: (gsap, els) => {
      const depths = [MOTION.parallax.far, MOTION.parallax.mid, MOTION.parallax.near];
      els.forEach((el, i) =>
        gsap.fromTo(
          el,
          { yPercent: depths[i] * 160 },
          {
            yPercent: -depths[i] * 160,
            duration: MOTION.dur.cinematic,
            ease: MOTION.ease.standard,
            yoyo: true,
            repeat: 1,
            overwrite: true,
          }
        )
      );
      return null;
    },
  },
];

function patternPanel(root, gsap) {
  root.innerHTML = `
    <div class="vp__lede">
      <p>
        The primitives the experiments are assembled from. Each card runs the
        real thing, lists the tokens it spends, and hands over the exact call —
        so a pattern moves from “approved in the lab” to “in the codebase”
        without anyone retyping a number.
      </p>
    </div>

    <div class="shelf">
      ${PATTERNS.map(
        (p) => `
        <article class="pat" data-pattern="${p.key}">
          <header class="pat__head">
            <h3 class="pat__name u-caps">${p.name}</h3>
            <button class="vp__btn" data-play>Run</button>
          </header>
          <p class="pat__use">${p.use}</p>
          <div class="pat__stage pat__stage--${p.key}" data-stage>${p.markup}</div>
          <ul class="pat__spends">${p.spends.map((t) => `<li><code>${t}</code></li>`).join('')}</ul>
          <details class="pat__code">
            <summary>Code</summary>
            <pre><code>${esc(p.code())}</code></pre>
            <button class="vp__btn" data-copy>Copy</button>
          </details>
        </article>`
      ).join('')}
    </div>
  `;

  function run(key) {
    const card = root.querySelector(`[data-pattern="${key}"]`);
    const stage = card.querySelector('[data-stage]');
    const els = gsap.utils.toArray(stage.querySelectorAll('[data-el]'));
    const pattern = PATTERNS.find((p) => p.key === key);
    gsap.killTweensOf(els);
    pattern.run(gsap, els, stage);
  }

  root.addEventListener('click', async (e) => {
    const playBtn = e.target.closest('[data-play]');
    if (playBtn) {
      run(playBtn.closest('[data-pattern]').dataset.pattern);
      return;
    }

    const copyBtn = e.target.closest('[data-copy]');
    if (copyBtn) {
      const key = copyBtn.closest('[data-pattern]').dataset.pattern;
      const code = PATTERNS.find((p) => p.key === key).code();
      try {
        await navigator.clipboard.writeText(code);
        copyBtn.textContent = 'Copied';
      } catch {
        copyBtn.textContent = 'See console';
        console.info(`[vocabulary] ${key}\n\n${code}`);
      }
      setTimeout(() => {
        copyBtn.textContent = 'Copy';
      }, 1800);
    }
  });

  // Run each specimen once as it scrolls into view, so the shelf reads as a
  // gallery rather than a wall of buttons waiting to be pressed.
  const seen = new WeakSet();
  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting || seen.has(entry.target) || isReduced()) return;
        seen.add(entry.target);
        run(entry.target.dataset.pattern);
      });
    },
    { threshold: 0.45 }
  );
  root.querySelectorAll('[data-pattern]').forEach((card) => io.observe(card));

  return { refresh: () => {}, run };
}

/* ===========================================================================
   Panel 4 · Libraries
   ----------------------------------------------------------------------------
   The lab is GSAP-first, but four other CDN libs are wired to the same
   motion.css tokens for jobs GSAP doesn't do cleanly. This panel exists so a
   reviewer can approve each addition by touching it, not by reading a
   bullet — the same rule that got us the Easing studio.

     Lenis          smooth-scroll with a tunable lerp; live toggle so you can
                    feel the difference at 0.05 vs 0.15 on this exact page.
     SplitType      run a phrase through the same line-mask reveal the hero
                    uses; the input becomes CMS copy in the real page.
     Lottie         bridge is wired in src/lottie.js; when a .lottie file is
                    authored, mountLottie() drops it in. Documented here with
                    the exact call and links to the experiment that would use it.
     Rive           same posture as Lottie — bridge in src/rive.js, ready for
                    a .riv artboard. Reserved for interactive stateful vector
                    (swatches, size pickers), not fire-and-forget confirmations.
   ======================================================================== */

import { initSmoothScroll, destroySmoothScroll, getLenis } from './scroll.js';
import { splitLines } from './split.js';

function libraryPanel(root, gsap) {
  root.innerHTML = `
    <div class="vp__lede">
      <p>
        Four CDN libs live alongside GSAP, each covering a job the token
        vocabulary couldn't reach on its own. Touch each one — a bullet list
        is easier to nod at than a live demo, and that's the difference this
        panel exists to make.
      </p>
      <p class="vp__aside">
        Every lib is loaded from a pinned CDN and reads
        <code>motion.css</code> through a bridge in <code>src/</code>. Nothing
        here reaches for a bundler; drop the <code>&lt;script&gt;</code> tags
        into a production page and the same call works.
      </p>
    </div>

    <div class="libs">
      <!-- SplitType — live -->
      <section class="lib">
        <header class="lib__head">
          <h3 class="lib__name u-caps">SplitType</h3>
          <p class="lib__what">Splits copy into lines/words/chars so GSAP can animate them one at a time. CMS-safe.</p>
        </header>
        <div class="lib__stage lib__stage--split">
          <label class="lib__field">
            <span>Phrase</span>
            <input type="text" data-split-input value="Colour drawn from the field. Cut for a twelve-hour shift." />
          </label>
          <div class="lib__preview" data-split-preview>
            <p class="lib__preview-copy" data-split-target>Colour drawn from the field. Cut for a twelve-hour shift.</p>
          </div>
          <button class="vp__btn vp__btn--go" data-split-play>Run</button>
        </div>
        <details class="lib__code">
          <summary>Bridge call</summary>
          <pre><code>import { splitLines } from './split.js';
const lines = splitLines('[data-mo-split="lines"]');
gsap.from(lines, {
  yPercent: 120,
  duration: MOTION.dur.deliberate,
  ease: MOTION.ease.entrance,
  stagger: MOTION.stagger.loose,
});</code></pre>
        </details>
      </section>

      <!-- Lenis — live -->
      <section class="lib">
        <header class="lib__head">
          <h3 class="lib__name u-caps">Lenis</h3>
          <p class="lib__what">Smooth-scroll layer that makes scrubbed timelines settle rather than snap. Opt-in per page.</p>
        </header>
        <div class="lib__stage lib__stage--lenis">
          <div class="lib__toggle">
            <span>Smooth scroll</span>
            <button class="vp__btn" type="button" data-lenis-toggle aria-pressed="false">Off</button>
          </div>
          <div class="lib__lerp">
            <label>
              <span>Lerp <b data-lenis-lerp-out>0.10</b></span>
              <input type="range" min="0.03" max="0.2" step="0.01" value="0.1" data-lenis-lerp />
            </label>
            <p class="lib__aside">
              Tuning the lerp writes <code>--mo-scroll-lerp</code>. Lower =
              weightier; higher = snappier. Below 0.05 starts to feel laggy
              on a trackpad.
            </p>
          </div>
          <button class="vp__btn vp__btn--go" data-lenis-scroll type="button">Scroll to top</button>
        </div>
        <details class="lib__code">
          <summary>Bridge call</summary>
          <pre><code>import { initSmoothScroll } from './scroll.js';
initSmoothScroll(ScrollTrigger);
// Lerp is driven by --mo-scroll-lerp; tune it here or in Token studio.</code></pre>
        </details>
      </section>

      <!-- Lottie — documented, awaiting asset -->
      <section class="lib">
        <header class="lib__head">
          <h3 class="lib__name u-caps">Lottie-web</h3>
          <p class="lib__what">After Effects vector animation. Best for confirmations, empty states, and mode toggles.</p>
        </header>
        <div class="lib__stage lib__stage--awaiting">
          <div class="lib__missing">
            <p>
              Bridge shipped; no <code>.lottie</code> asset is authored yet. Once one exists,
              mount it with the call below — the cart drawer's success flash
              (experiment 06) is where it plugs in first.
            </p>
            <a class="vp__btn" href="/experiments/06-cart-drawer/">Open experiment 06 &rarr;</a>
          </div>
        </div>
        <details class="lib__code">
          <summary>Bridge call</summary>
          <pre><code>import { mountLottie } from './lottie.js';
mountLottie('[data-lottie="check"]', '/lottie/check.json', {
  trigger: 'manual', loop: false,
});
// .play() when the moment happens — playback speed follows the toolbar Speed.</code></pre>
        </details>
      </section>

      <!-- Rive — documented, awaiting asset -->
      <section class="lib">
        <header class="lib__head">
          <h3 class="lib__name u-caps">Rive</h3>
          <p class="lib__what">State-machine vector. Reach for it when the vector needs to react — swatches, size pickers, morphing icons.</p>
        </header>
        <div class="lib__stage lib__stage--awaiting">
          <div class="lib__missing">
            <p>
              Bridge shipped; no <code>.riv</code> artboard is authored yet.
              The fabric badge in experiment 07 marks where Rive plugs in
              first — hover states, selection morph, tap feedback all on one
              runtime.
            </p>
            <a class="vp__btn" href="/experiments/07-collection-landing/">Open experiment 07 &rarr;</a>
          </div>
        </div>
        <details class="lib__code">
          <summary>Bridge call</summary>
          <pre><code>import { mountRive } from './rive.js';
const swatch = mountRive('[data-rive="swatch"]', '/rive/swatch.riv', {
  stateMachine: 'Selector',
  artboard: 'Swatch',
});
swatch.input('hovered')?.fire();</code></pre>
        </details>
      </section>

      <!-- Reference — Material dossier -->
      <section class="lib lib--reference">
        <header class="lib__head">
          <h3 class="lib__name u-caps">Material motion — reference</h3>
          <p class="lib__what">A one-page synthesis of Google Material's motion principles. Not runtime code; shared vocabulary for review.</p>
        </header>
        <div class="lib__stage lib__stage--reference">
          <p>
            Where a FIGS token disagrees with a Material recommendation, the
            disagreement is deliberate. The dossier is where those
            disagreements are named (overshoot, cinematic durations, ease-in
            on exits) so a reviewer arguing with a curve has a shared
            baseline to argue from.
          </p>
          <a class="vp__btn" href="/docs/reference/material-motion.md" target="_blank" rel="noopener">Read the dossier &rarr;</a>
        </div>
      </section>
    </div>
  `;

  /* --- SplitType demo ---------------------------------------------------- */
  const splitInput = root.querySelector('[data-split-input]');
  const splitTarget = root.querySelector('[data-split-target]');
  const splitPreview = root.querySelector('[data-split-preview]');

  function runSplit() {
    if (!window.SplitType) {
      splitPreview.innerHTML =
        '<p class="lib__missing">SplitType did not load. Check the CDN <code>&lt;script&gt;</code>.</p>';
      return;
    }
    // Rebuild the target from the input so a retype resets cleanly.
    splitPreview.innerHTML = `<p class="lib__preview-copy" data-split-target>${esc(splitInput.value)}</p>`;
    const el = splitPreview.querySelector('[data-split-target]');
    const lines = splitLines(el);
    if (!lines.length) return;
    gsap.fromTo(
      lines,
      { yPercent: 120 },
      {
        yPercent: 0,
        duration: MOTION.dur.deliberate,
        ease: MOTION.ease.entrance,
        stagger: MOTION.stagger.loose,
        overwrite: true,
      }
    );
  }

  root.querySelector('[data-split-play]').addEventListener('click', runSplit);
  splitInput.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') runSplit();
  });

  /* --- Lenis toggle ------------------------------------------------------ */
  const lenisToggle = root.querySelector('[data-lenis-toggle]');
  const lenisLerp = root.querySelector('[data-lenis-lerp]');
  const lenisLerpOut = root.querySelector('[data-lenis-lerp-out]');

  function reflectLenisState() {
    const on = !!getLenis();
    lenisToggle.textContent = on ? 'On' : 'Off';
    lenisToggle.setAttribute('aria-pressed', String(on));
  }

  lenisToggle.addEventListener('click', () => {
    if (getLenis()) destroySmoothScroll();
    else initSmoothScroll(window.ScrollTrigger);
    reflectLenisState();
  });

  lenisLerp.addEventListener('input', () => {
    const value = Number(lenisLerp.value).toFixed(2);
    lenisLerpOut.textContent = value;
    // Push the token so BOTH the CSS var and any live Lenis instance can read
    // it. The bridge re-reads on next lerp check.
    document.documentElement.style.setProperty('--mo-scroll-lerp', value);
    const instance = getLenis();
    if (instance) instance.options.lerp = Number(value);
  });

  root.querySelector('[data-lenis-scroll]').addEventListener('click', () => {
    const instance = getLenis();
    if (instance) instance.scrollTo(0);
    else window.scrollTo({ top: 0, behavior: 'smooth' });
  });

  reflectLenisState();

  return {
    refresh: () => {
      reflectLenisState();
      // Re-read the lerp value in case Token studio changed it.
      const css = getComputedStyle(document.documentElement);
      const raw = parseFloat(css.getPropertyValue('--mo-scroll-lerp')) || 0.1;
      lenisLerp.value = String(raw);
      lenisLerpOut.textContent = raw.toFixed(2);
    },
  };
}

/* ===========================================================================
   Mount
   ======================================================================== */

const TABS = [
  ['easing', 'Easing studio', easingPanel],
  ['tokens', 'Token studio', tokenPanel],
  ['patterns', 'Pattern shelf', patternPanel],
  ['libraries', 'Libraries', libraryPanel],
];

export function mountVocabulary({ root, gsap }) {
  const tabList = root.querySelector('[data-vocab-tabs]');
  const panels = new Map();

  tabList.innerHTML = TABS.map(
    ([key, label], i) => `
    <button class="vocab__tab" role="tab" data-tab="${key}"
            aria-selected="${i === 0}" aria-controls="vocab-panel-${key}">
      ${label}
    </button>`
  ).join('');

  TABS.forEach(([key, , build]) => {
    const panel = root.querySelector(`[data-panel="${key}"]`);
    panel.id = `vocab-panel-${key}`;
    panel.setAttribute('role', 'tabpanel');
    panels.set(key, { panel, api: build(panel, gsap) });
  });

  function show(key) {
    panels.forEach(({ panel }, k) => {
      panel.hidden = k !== key;
    });
    tabList.querySelectorAll('[data-tab]').forEach((b) =>
      b.setAttribute('aria-selected', String(b.dataset.tab === key))
    );
    panels.get(key).api.refresh?.();
    try {
      localStorage.setItem('figs-vocab-tab', key);
    } catch {
      /* no-op */
    }
  }

  tabList.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-tab]');
    if (btn) show(btn.dataset.tab);
  });

  let initial = 'easing';
  try {
    const saved = localStorage.getItem('figs-vocab-tab');
    if (saved && panels.has(saved)) initial = saved;
  } catch {
    /* no-op */
  }
  show(initial);

  // Reduced-motion toggle and token edits both fire mo:replay. Re-read the
  // panels so the printed values never disagree with what just ran.
  window.addEventListener('mo:replay', () => panels.forEach(({ api }) => api.refresh?.()));

  return { show, hasOverrides };
}
